// src/components/blog/BlogCard.jsx
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiPencil, HiTrash, HiEllipsisVertical } from 'react-icons/hi2';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const formatDistanceToNow = (date) => {
  const now = new Date();
  const diff = Math.abs(now - new Date(date));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days < 1) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (days < 30) {
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else {
    const months = Math.floor(days / 30);
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  }
};

const BlogCard = ({ blog, onBlogDeleted }) => {
  const { user, canModifyBlog, deleteBlog, isModerator } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();

  const imageUrl = blog.cover_image?.startsWith('/media')
    ? `https://app.writeapp.in${blog.cover_image}`
    : blog.cover_image;

  const authorName = blog.author?.name || blog.author?.email || 'Anonymous';
  const authorId = blog.author?.id || '';
  const likes = blog.like_count || 0;
  const tags = blog.tags?.map(tag => tag.name) || [];

  // Check ownership and permissions
  const blogUserId = blog.user_id || blog.author?.id;
  const currentUserId = user?.id || user?.user_id || user?.userId || user?.pk;
  const isBlogOwner = currentUserId && blogUserId &&
    (String(currentUserId) === String(blogUserId) || currentUserId === blogUserId);
  const canModify = canModifyBlog(blog);
  const userIsModerator = isModerator();

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this blog? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteBlog(blog.id);
      if (result.success) {
        toast.success('Blog deleted successfully');
        // Call parent callback to refresh the blog list
        if (onBlogDeleted) {
          onBlogDeleted(blog.id);
        }
      } else {
        toast.error(result.message || 'Failed to delete blog');
      }
    } catch (error) {
      toast.error('An error occurred while deleting the blog');
      console.error('Delete error:', error);
    } finally {
      setIsDeleting(false);
      setShowDropdown(false);
    }
  };

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/blog/edit/${blog.id}`);
    setShowDropdown(false);
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  return (
    <motion.div
      className="bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden shadow-lg border border-gray-700 hover:border-gray-500 transition-all duration-300 relative"
      whileHover={{ y: -5 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Dropdown menu for edit/delete */}
      {user && canModify && (
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={toggleDropdown}
            className="p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            disabled={isDeleting}
          >
            <HiEllipsisVertical className="w-4 h-4" />
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-32 bg-gray-800 border border-gray-600 rounded-md shadow-lg">
              {isBlogOwner && (
                <button
                  onClick={handleEdit}
                  className="w-full px-4 py-2 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2"
                >
                  <HiPencil className="w-4 h-4" />
                  Edit
                </button>
              )}

              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
              >
                <HiTrash className="w-4 h-4" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          )}
        </div>
      )}

      <Link to={`/blog/${blog.id}`}>
        <div className="relative h-48 overflow-hidden">
          <img
            src={imageUrl}
            alt={blog.title}
            className="w-full h-full object-cover transition-transform duration-500"
            style={{ transform: isHovered ? 'scale(1.05)' : 'scale(1)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          <div className="absolute bottom-0 left-0 p-4 w-full">
            <div className="flex justify-between items-center">
              <div className="flex items-center text-sm text-gray-300">
                <span>{formatDistanceToNow(blog.created_at)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="flex items-center text-sm text-gray-300">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {likes}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-xl font-semibold mb-2 line-clamp-2">{blog.title}</h3>
          <p className="text-gray-400 text-sm mb-3 line-clamp-3">{blog.summary || blog.excerpt || ''}</p>
          <div className="flex items-center justify-between">
            <span className="text-gray-400 text-sm">
              @{authorName}
            </span>
            <div className="flex flex-wrap gap-2">
              {tags.slice(0, 2).map((tag, index) => (
                <span key={index} className="text-xs px-2 py-1 bg-gray-700 rounded-full text-gray-300">
                  #{tag}
                </span>
              ))}
              {tags.length > 2 && (
                <span className="text-xs px-2 py-1 bg-gray-700 rounded-full text-gray-300">
                  +{tags.length - 2}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </motion.div>
  );
};

export default BlogCard;